import {
  datetime,
  parseZone as datetimeParseZone,
  type DatetimeType,
} from '@common/services/datetime';
import type { Moment } from 'moment';

import { getShadowEnabled } from './flag-source';
import { emitShadowCallCounter, type MomentOpType } from './metrics';
import { reportShadowMismatch } from './mismatch-reporter';
import { sendMatchSample } from './sample-sender';
// Imported for its runtime value (call-time only — see wrapConstructed). The
// shadow-runner ↔ shadow-moment cycle is safe because both modules reference each
// other's bindings inside functions invoked at runtime, never during module eval.
import { wrapShadowMoment } from './shadow-moment';
import type { DomainNameType } from './subdomains';

const THREW = '<threw>';

// moment and dayjs format an invalid date to the same sentinel in different
// casing — moment "Invalid date", dayjs "Invalid Date". The strings are
// functionally identical and no consumer branches on their exact text, so this
// divergence is accepted as benign (WORK-6090, M5.2). We canonicalize the
// sentinel before comparing the two sides, so the casing alone never counts as
// a shadow mismatch (it is treated as a match and is not sent to Sentry). The
// raw outputs are still recorded verbatim in the match-sample payload.
const INVALID_DATE_SENTINEL = /^invalid date$/i;

// moment renders the UTC zero-offset as `+00:00`; dayjs in UTC mode renders the
// identical instant as `Z`. The two are exactly equivalent designators for UTC
// and no consumer branches on the difference — the same benign-quirk class as the
// `Invalid date` casing above (WORK-6090, M5.2). Canonicalize a trailing `Z` to
// `+00:00` before comparing so this notation alone never counts as a mismatch.
// A genuinely different zone (`+05:00`, `-08:00`) is left untouched and still
// diverges. The raw outputs are recorded verbatim in the payload.
const TRAILING_UTC_Z = /Z$/;

// dayjs renders the `z` timezone-abbreviation token as a `GMT±H[:mm]` string
// (`GMT-3`, `GMT+8:45`) where moment-timezone renders the IANA offset form
// (`-03`, `+0845`); for US zones both already emit the same named abbreviation
// (`EST`, `CST`, …), which is why only an offshore `-03` user tripped WORKS-33B.
// This `z`-token divergence is accepted as benign and deliberately NOT fixed in
// the wrapper (WORK-6090, M5.2 — WORKS-33B): `GMT-3` is the post-cutover norm and
// reproducing moment's form would mean bundling moment-timezone's abbreviation
// database into the dayjs wrapper. Canonicalize dayjs's `GMT±H[:mm]` form to
// moment's `±HH[mm]` form before comparing so this divergence alone never counts
// as a mismatch — the same benign-quirk class as the `Invalid date` casing and
// the `Z` vs `+00:00` notation above. moment never emits the `GMT±H` form, so
// only the dayjs side is rewritten and a genuinely different instant still
// diverges; raw outputs are recorded verbatim in the payload. (A zone whose
// moment abbreviation is a name dayjs cannot produce — e.g. `IST` vs `GMT+5:30`
// → `+0530` — is a separate, rarer divergence and is left to diverge.)
const DAYJS_GMT_OFFSET_ABBREV = /GMT([+-])(\d{1,2})(?::?(\d{2}))?/g;

function canonicalizeZoneAbbrev(serialized: string): string {
  return serialized.replace(
    DAYJS_GMT_OFFSET_ABBREV,
    (_match, sign: string, hours: string, minutes?: string) => {
      const mm = minutes && minutes !== '00' ? minutes : '';
      return `${sign}${hours.padStart(2, '0')}${mm}`;
    }
  );
}

function normalizeForComparison(serialized: string): string {
  if (INVALID_DATE_SENTINEL.test(serialized)) {
    return 'Invalid date';
  }
  return canonicalizeZoneAbbrev(serialized).replace(TRAILING_UTC_Z, '+00:00');
}

// A trailing timezone-label token emitted by the `z`/`zz` format token, always
// space-separated from the wall-clock portion: a named abbreviation (`EST`,
// `CST`), dayjs's `GMT±H[:mm]` / moment's numeric `±HH[mm]` offset form, or —
// for a zone-less parsed instant moment cannot label — EMPTY (just the literal
// space the format string places before `z`). The leading space is required, so
// a glued ISO offset (`…08:30:00+05:00`, no preceding space) is deliberately NOT
// matched: a genuinely different zone still diverges through the prefix.
const TRAILING_ZONE_LABEL =
  / (GMT[+-]\d{1,2}(?::?\d{2})?|[+-]\d{2,4}|[A-Z]{2,5})?$/;

function splitTrailingZoneLabel(serialized: string): {
  prefix: string;
  label: string | null;
} {
  const match = TRAILING_ZONE_LABEL.exec(serialized);
  if (!match) {
    return { prefix: serialized, label: null };
  }
  return { prefix: serialized.slice(0, match.index), label: match[1] ?? '' };
}

// moment renders the `z` token EMPTY for a zone-less parsed instant
// (`M/D/YY, h:mmA z` → `"12/4/23, 3:58PM "` — trailing space, no abbreviation),
// while the dayjs wrapper's timezone/advancedFormat plugin resolves the
// system-zone abbreviation (`"12/4/23, 3:58PM EST"`). canonicalizeZoneAbbrev
// cannot bridge this — there is no `GMT±H` form to rewrite, the moment side is
// simply empty. It is the same benign-quirk class as the `Invalid date` casing,
// the `Z` ↔ `+00:00` notation, and the `GMT-3` ↔ `-03` abbreviation above
// (WORK-6090, M5.2 — WORKS-33B, here in its empty-vs-named manifestation): the
// wall-clock is identical and no consumer branches on the zone label's presence.
// We accept it ONLY when the normalized strings differ solely in their trailing
// zone-label token AND at least one side's token is empty — two non-empty but
// different labels (`-03` vs `-04`) are a genuinely different offset and still
// diverge, and a different wall-clock still diverges through the prefix. Raw
// outputs are recorded verbatim in the match-sample payload.
function serializedOutputsMatch(
  momentSerialized: string,
  dayjsSerialized: string
): boolean {
  const momentNormalized = normalizeForComparison(momentSerialized);
  const dayjsNormalized = normalizeForComparison(dayjsSerialized);

  if (momentNormalized === dayjsNormalized) {
    return true;
  }

  const momentParts = splitTrailingZoneLabel(momentNormalized);
  const dayjsParts = splitTrailingZoneLabel(dayjsNormalized);

  if (momentParts.prefix !== dayjsParts.prefix) {
    return false;
  }

  const momentLabelEmpty = !momentParts.label;
  const dayjsLabelEmpty = !dayjsParts.label;

  return momentLabelEmpty || dayjsLabelEmpty;
}

export type ShadowSerializerType<MR, DR> = {
  // Name of the moment method whose output the two sides are compared on
  // (e.g. 'format', 'toISOString'). `op` records how the value was
  // constructed; `method` records which call's output diverged.
  method: string;
  moment: (result: MR) => string;
  dayjs: (result: DR) => string;
};

export type ShadowRunArgsType<MR, DR> = {
  op: MomentOpType;
  domain: DomainNameType;
  subdomain: string;
  inputs: ReadonlyArray<unknown>;
  computeMoment: () => MR;
  computeDayjs: () => DR;
  serialize: ShadowSerializerType<MR, DR>;
};

// Construction ops whose result is a Moment (not a Duration). Their result is
// returned as a Proxy-wrapped pair so chained ops on it are shadowed too (M3.4).
// `duration` is excluded — it produces a Duration, which has no chained-arithmetic
// surface worth shadowing and is compared on `humanize` at construction instead.
const CONSTRUCTION_PRODUCES_MOMENT = new Set<MomentOpType>([
  'moment',
  'tz',
  'parseZone',
]);

// Only object results can be Proxy targets. Production construction ops always
// produce Moment / Dayjs objects; this guard keeps the runner robust to the
// primitive stand-ins the unit specs feed in for the comparison-only path.
function isProxyable(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

export function runShadow<MR, DR>(args: ShadowRunArgsType<MR, DR>): MR {
  if (!getShadowEnabled()) {
    return args.computeMoment();
  }

  const momentResult = args.computeMoment();

  let dayjsResult: DR | undefined;
  let dayjsSerialized: string;
  let dayjsThrew = false;
  try {
    dayjsResult = args.computeDayjs();
    dayjsSerialized = args.serialize.dayjs(dayjsResult);
  } catch {
    dayjsThrew = true;
    dayjsSerialized = THREW;
  }

  const momentSerialized = args.serialize.moment(momentResult);

  compareAndEmit({
    op: args.op,
    domain: args.domain,
    subdomain: args.subdomain,
    method: args.serialize.method,
    inputs: args.inputs,
    momentSerialized,
    dayjsSerialized,
    dayjsThrew,
  });

  // Hand the already-computed pair to the chainable proxy so the dayjs side is
  // not discarded — the pair starts in sync. If the dayjs side threw there is no
  // peer to carry, so fall back to the raw Moment (chained ops stay Moment-only).
  if (
    dayjsResult !== undefined &&
    !dayjsThrew &&
    CONSTRUCTION_PRODUCES_MOMENT.has(args.op) &&
    isProxyable(momentResult) &&
    isProxyable(dayjsResult)
  ) {
    return wrapShadowMoment(
      {
        m: momentResult as unknown as Moment,
        d: dayjsResult as unknown as DatetimeType,
      },
      { domain: args.domain, subdomain: args.subdomain }
    ) as unknown as MR;
  }

  return momentResult;
}

export type CompareAndEmitArgsType = {
  op: MomentOpType;
  domain: DomainNameType;
  subdomain: string;
  // Moment method whose output is being compared (e.g. 'format', 'add', 'diff').
  method: string;
  inputs: ReadonlyArray<unknown>;
  momentSerialized: string;
  dayjsSerialized: string;
  dayjsThrew: boolean;
};

// The shared comparison core used by both the M3.1 construction runner above and
// the M3.4 chained-op proxy (shadow-moment.ts): normalize, emit the call counter,
// then route to a low-rate match sample or an always-on mismatch report. Callers
// pass already-serialized outputs, so this never re-enters the proxy.
export function compareAndEmit(args: CompareAndEmitArgsType): void {
  const match =
    !args.dayjsThrew &&
    serializedOutputsMatch(args.momentSerialized, args.dayjsSerialized);

  const tags = {
    domain: args.domain,
    subdomain: args.subdomain,
    op: args.op,
  };

  emitShadowCallCounter({ ...tags, match: match ? 'true' : 'false' });

  const inputFingerprint = fingerprint({ op: args.op, args: args.inputs });
  const payload = {
    ...tags,
    method: args.method,
    inputFingerprint,
    inputs: serializeInputs(args.inputs),
    momentOutput: args.momentSerialized,
    dayjsOutput: args.dayjsSerialized,
  };

  if (match) {
    sendMatchSample(payload);
  } else {
    reportShadowMismatch(payload);
  }
}

// Default serializers used by the four entry-point ops. Each wrapper file
// (factory.ts, tz.ts, parse-zone.ts, duration.ts) picks the serializer
// matching its op's output shape.

export const formatSerializer: ShadowSerializerType<Moment, DatetimeType> = {
  method: 'format',
  moment: (m) => safe(() => m.format()),
  dayjs: (d) => safe(() => d.format()),
};

type DurationLikeType = { humanize: (withSuffix?: boolean) => string };

// Durations are compared on `humanize(true)`, not `toISOString()`. Every
// production consumer of `moment.duration(...)` renders via `.humanize()` /
// `.humanize(true)` (the mission-control lead-time badges); none serializes a
// duration to ISO 8601. moment and dayjs agree on humanized output but diverge
// on `toISOString()` purely in unit normalization — moment keeps the time
// portion flat (`PT1404H16M50S`) while dayjs's duration plugin rolls hours up
// into calendar months/days (`P1M28DT2H16M50S`) for the same absolute span.
// Comparing on `toISOString()` therefore flagged a method no consumer calls,
// producing pure shadow noise (WORK-6090). `humanize(true)` is the dominant
// call shape and a strict superset of the no-arg form, so it is the canonical
// consumer-relevant surface to gate the cutover on.
export const durationSerializer: ShadowSerializerType<
  DurationLikeType,
  DurationLikeType
> = {
  method: 'humanize',
  moment: (m) => safe(() => m.humanize(true)),
  dayjs: (d) => safe(() => d.humanize(true)),
};

// Exposed for parity helpers that need to build a dayjs equivalent for the
// shadow side. shadow-runner.ts is the only file in the wrapper module that
// imports from @common/services/datetime; this re-export keeps that rule
// honest while still letting the per-op wrappers compose dayjs calls.
export const dayjsApi = {
  datetime,
  parseZone: datetimeParseZone,
};

function safe(fn: () => string): string {
  try {
    return fn();
  } catch {
    return THREW;
  }
}

// 8-hex-char FNV-1a fingerprint of a stable JSON encoding of (op, args).
// Non-cryptographic — the goal is bucketing repeated mismatches from the same
// input shape, not user identification. The mismatch payload contract in
// 01-metrics-tooling.md calls for SHA-256; FNV-1a satisfies the same intent
// without an async crypto round-trip on every wrapped call.
function fingerprint(input: {
  op: MomentOpType;
  args: ReadonlyArray<unknown>;
}) {
  const serialized = stableStringify(input);
  let hash = 0x811c9dc5;
  for (let i = 0; i < serialized.length; i++) {
    hash ^= serialized.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0').slice(0, 8);
}

// The mismatch/sample payload carries the actual runShadow inputs (not just
// their fingerprint) so a divergence is reproducible from the Sentry event
// without guessing what produced it. Inputs are date/format/timezone arguments
// — bounded and non-PII — but we still cap the serialized length so a
// pathological argument can't bloat the event.
const MAX_SERIALIZED_INPUTS = 1000;

function serializeInputs(inputs: ReadonlyArray<unknown>): string {
  const serialized = stableStringify(inputs);
  return serialized.length > MAX_SERIALIZED_INPUTS
    ? `${serialized.slice(0, MAX_SERIALIZED_INPUTS)}…<truncated>`
    : serialized;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, v) => {
    if (typeof v === 'bigint') return v.toString();
    if (
      v &&
      typeof v === 'object' &&
      typeof (v as Moment).toISOString === 'function'
    ) {
      return (v as Moment).toISOString();
    }
    return v;
  });
}
